import { useTranslation } from "@/hooks";
import type { EnhancementResultsData } from "@/types/enhance";
import { Badge, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef } from "react";

// Re-export for backward compatibility
export type { EnhancementResultsData };

export function EnhancementResultsModal({
  opened,
  onClose,
  data,
}: {
  opened: boolean;
  onClose: () => void;
  data: EnhancementResultsData;
}) {
  const { t } = useTranslation();
  const gridRef = useRef<AgGridReact>(null);

  const successPercentage = useMemo(() => {
    if (data.totalCount === 0) return 0;
    return Math.round((data.successCount / data.totalCount) * 100);
  }, [data.successCount, data.totalCount]);

  const durationSeconds = useMemo(() => {
    return (data.durationMs / 1000).toFixed(2);
  }, [data.durationMs]);

  // Prepare AG Grid data
  const { rowData, colDefs } = useMemo(() => {
    // Get all unique output field keys
    const outputFieldKeys = new Set<string>();
    data.results.forEach((result) => {
      if (result.success) {
        Object.keys(result.outputFields).forEach((key) =>
          outputFieldKeys.add(key)
        );
      }
    });

    // Get all unique input field keys (from inputData)
    const inputFieldKeys = new Set<string>();
    data.results.forEach((result) => {
      Object.keys(result.inputData || {}).forEach((key) =>
        inputFieldKeys.add(key)
      );
    });

    // Build rows with input data, output fields, and status
    const rows = data.results.map((item) => {
      const row: Record<string, any> = {
        rowIndex: item.rowIndex + 1,
        status: item.success
          ? t("enhancement").success
          : t("enhancement").failed,
        error: item.error || "—",
      };

      // Add input data fields (limit to avoid too many columns)
      const inputKeysArray = Array.from(inputFieldKeys).slice(0, 5);
      inputKeysArray.forEach((key) => {
        row[`input_${key}`] = item.inputData[key] ?? "—";
      });

      // Add output fields
      outputFieldKeys.forEach((key) => {
        row[key] = item.outputFields[key] ?? "—";
      });

      return row;
    });

    // Build column definitions
    const columns: ColDef[] = [
      {
        field: "rowIndex",
        headerName: t("enhancement").rowNumber,
        sortable: true,
        pinned: "left",
      },
      {
        field: "status",
        headerName: t("enhancement").status,
        sortable: true,
        filter: true,
        pinned: "left",
        cellRenderer: (params: any) => {
          const status = params.value;
          if (status === t("enhancement").success) {
            return (
              <Badge color="green" leftSection={<IconCheck size={12} />}>
                {t("enhancement").success}
              </Badge>
            );
          } else if (status === t("enhancement").failed) {
            return (
              <Badge color="red" leftSection={<IconX size={12} />}>
                {t("enhancement").failed}
              </Badge>
            );
          }
          return <Badge color="gray">{status}</Badge>;
        },
      },
    ];

    // Add input columns (up to 5)
    const inputKeysArray = Array.from(inputFieldKeys).slice(0, 5);
    inputKeysArray.forEach((key) => {
      columns.push({
        field: `input_${key}`,
        headerName: `${t("enhancement").input}: ${key}`,
        sortable: true,
        filter: true,
      });
    });

    // Add output columns
    outputFieldKeys.forEach((key) => {
      columns.push({
        field: key,
        headerName: key,
        sortable: true,
        filter: true,
      });
    });

    // Add error column at the end
    columns.push({
      field: "error",
      headerName: t("enhancement").error,
      sortable: true,
      filter: true,
      cellStyle: (params: any) => {
        if (params.value !== "—") {
          return { color: "var(--mantine-color-red-6)" };
        }
        return null;
      },
    });

    return { rowData: rows, colDefs: columns };
  }, [data.results, t]);

  // Auto-size columns when grid is ready or data changes
  useEffect(() => {
    if (opened && gridRef.current?.api) {
      // Use setTimeout to ensure grid is fully rendered
      setTimeout(() => {
        gridRef.current?.api.autoSizeAllColumns();
      }, 100);
    }
  }, [opened, data.results]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${t("enhancement").results} - ${data.serviceName}`}
      centered
      size="90vw"
      styles={{
        body: { height: "70vh", display: "flex", flexDirection: "column" },
      }}
    >
      <Stack gap="md" style={{ height: "100%" }}>
        {/* Statistics */}
        <Group justify="space-between" wrap="wrap">
          <Stack gap={4}>
            <Text size="sm" c="gray.9">
              {t("enhancement").totalRows}
            </Text>
            <Text size="xl" fw={700}>
              {data.totalCount}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text size="sm" c="gray.9">
              {t("enhancement").successful}
            </Text>
            <Text size="xl" fw={700} c="green">
              {data.successCount}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text size="sm" c="gray.9">
              {t("enhancement").failed}
            </Text>
            <Text size="xl" fw={700} c="red">
              {data.failedCount}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text size="sm" c="gray.9">
              {t("enhancement").successRate}
            </Text>
            <Text size="xl" fw={700}>
              {successPercentage}%
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text size="sm" c="gray.9">
              {t("enhancement").duration}
            </Text>
            <Text size="xl" fw={700}>
              {durationSeconds}s
            </Text>
          </Stack>
        </Group>

        {/* Results Table */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            width: "100%",
            overflow: "auto",
          }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={{
              resizable: true,
            }}
            onGridReady={(params) => {
              params.api.autoSizeAllColumns();
            }}
            domLayout="normal"
          />
        </div>

        <Group justify="flex-end">
          <Button onClick={onClose}>{t("common").close}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
